// Cliente único da API do console: tipado pelo contrato, com tratamento central de erro,
// cancelamento (AbortSignal do TanStack Query) e medição de latência por rota.
import createClient, { type Middleware } from "openapi-fetch";
import type { paths as core } from "./schema/core";
import type { paths as acompanhar } from "./schema/acompanhar";
import type { paths as operar } from "./schema/operar";
import type { paths as configurar } from "./schema/configurar";
import type { paths as interno } from "./schema/interno";

import { recordApiLatency } from "@/perf/vitals";

/** Área ainda sem rotas é gerada como `Record<string, never>`, que zeraria a interseção. */
type Area<T> = string extends keyof T ? unknown : T;
/** O contrato é dividido por área (openapi/*.yaml); o cliente enxerga a união. */
export type paths = core & Area<acompanhar> & Area<operar> & Area<configurar> & Area<interno>;

export const API_BASE = `${import.meta.env.VITE_API_URL ?? ""}/console/v1`;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail?: string,
  ) {
    super(detail ? `${title}: ${detail}` : title);
    this.name = "ApiError";
  }
  get isAuth() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  /** Edição concorrente: outra pessoa salvou antes. A tela recarrega e mostra o conflito. */
  get isConflict() {
    return this.status === 409;
  }
}

type MaybePromise<T> = T | Promise<T>;

interface ApiAuth {
  /** Token da próxima chamada (o do Cognito é renovado antes de vencer). */
  getToken: () => MaybePromise<string | null>;
  /** Depois de um 401: tenta renovar uma vez. `null` = a sessão acabou. */
  renew?: () => Promise<string | null>;
  /** Cabeçalhos extras por chamada (ex.: persona da demonstração, só no mock). */
  headers?: () => Record<string, string | null>;
  onUnauthorized: () => void;
}

let auth: ApiAuth = { getToken: () => null, onUnauthorized: () => {} };

export function configureApi(opts: ApiAuth) {
  auth = opts;
}

const started = new WeakMap<Request, number>();
/** Cópia do pedido antes do envio, para repetir uma vez depois de renovar o token. */
const retryable = new WeakMap<Request, Request>();

const middleware: Middleware = {
  async onRequest({ request }) {
    const token = await auth.getToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    for (const [k, v] of Object.entries(auth.headers?.() ?? {})) if (v) request.headers.set(k, v);
    if (auth.renew) retryable.set(request, request.clone());
    started.set(request, performance.now());
    return request;
  },
  async onResponse({ request, response: first, schemaPath }) {
    const t0 = started.get(request);
    if (t0 !== undefined) recordApiLatency(`${request.method} ${schemaPath}`, performance.now() - t0);
    let response = first;
    // 401 com sessão renovável: renova uma vez e repete o pedido antes de mandar para o login.
    const copy = retryable.get(request);
    if (response.status === 401 && copy && auth.renew) {
      const token = await auth.renew().catch(() => null);
      if (token) {
        copy.headers.set("Authorization", `Bearer ${token}`);
        response = await apiFetch(copy);
      }
    }
    if (response.ok) return response;
    let title = response.statusText || "Erro";
    let detail: string | undefined;
    try {
      const body = (await response.clone().json()) as { title?: string; detail?: string };
      title = body.title ?? title;
      detail = body.detail;
    } catch {
      /* corpo não-JSON: fica o statusText */
    }
    if (response.status === 401) auth.onUnauthorized();
    throw new ApiError(response.status, title, detail);
  },
};

// `fetch` resolvido a cada chamada (não capturado na criação): quem intercepta a rede
// depois do boot — MSW em teste, extensões, polyfills — continua valendo.
/**
 * Transporte da API. Com `VITE_MOCKS=on` o mock responde aqui mesmo, na página (mocks/browser.ts),
 * sem service worker: reload forçado ou DevTools com cache desligado pulavam o worker e o `/me` caía
 * no index.html do CloudFront. Fora do mock é o `fetch` do navegador.
 */
let transport: ((req: Request) => Promise<Response>) | null = null;
export function setApiTransport(fn: ((req: Request) => Promise<Response>) | null) {
  transport = fn;
}
const apiFetch = (req: Request) => (transport ? transport(req) : globalThis.fetch(req));

export const api = createClient<paths>({ baseUrl: API_BASE, fetch: apiFetch });
api.use(middleware);

/** Desembrulha `{ data, error }` do openapi-fetch; erro HTTP já virou ApiError no middleware. */
export async function unwrap<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await p;
  if (error !== undefined) throw new ApiError(response.status, response.statusText);
  return data as T;
}
